import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Section, SectionContainer, SectionHeader } from "@/components/ui/section";
import { FeatureGrid } from "@/components/ui/feature-grid";
import { CtaSection } from "@/components/ui/cta-section";
import { DisclaimerNotice } from "@/components/ui/disclaimer-notice";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, Shield, Zap, FileText, Clock, Award, Gavel, Target, ShieldAlert, Star, Search, Upload, CheckCircle, ClipboardList } from "lucide-react";
import { Link } from "react-router-dom";
// Hero image in WebP for smaller size and faster LCP
const heroImage = "/images/hero-image.webp";
import { useDeviceType } from "@/hooks/use-device-type";
import { MobileAppLayout } from "@/components/mobile/MobileAppLayout";
import { MobileHeroSection } from "@/components/mobile/MobileHeroSection";
import { MobileFeatureCard } from "@/components/mobile/MobileFeatureCard";
import { MobilePriceCard } from "@/components/mobile/MobilePriceCard";
import { PackageSelector } from "@/components/PackageSelector";
import { FileUpload } from "@/components/FileUpload";
import { getPackageById } from "@/config/packages";
import { useDynamicPricing } from "@/hooks/useDynamicPricing";
import { useDatabaseStats } from "@/hooks/useDatabaseStats";
import { BusinessAccessCTA } from "@/components/BusinessAccessCTA";

const Index = () => {
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const { packages: dynamicPackages } = useDynamicPricing();
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isMobile,
    isLandscape
  } = useDeviceType();
  const getPackageDisplay = (packageId: string): string => {
    const pkg = getPackageById(packageId);
    return pkg?.name || packageId;
  };
  const handleFileUpload = async (file: File) => {
    // Speichere die Datei als Base64 im sessionStorage für die Upload-Seite
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      sessionStorage.setItem('pendingUploadFile', JSON.stringify({
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64
      }));
      // Schließe Dialog und navigiere zur Upload-Seite
      setShowUploadDialog(false);
      localStorage.setItem('selectedPackage', selectedPackage);
      navigate('/speisekarte-hochladen');
    };
    reader.readAsDataURL(file);
  };
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
      }
    }
  }, [location.hash]);
  const detailedFunctions = [{
    icon: Target,
    title: "14 Allergene automatisch",
    description: <>Erkennung aller <Link to="/kennzeichnung#allergene-zusatzstoffe" className="text-primary hover:underline">14 EU-weit vorgeschriebenen Allergene gemäß LMIV Anhang II</Link> mit präziser Kennzeichnung.</>,
    items: ["Weizen, Roggen, Gerste", "Krebstiere, Fische", "Milch, Eier", "Haselnuss und weitere"]
  }, {
    icon: Shield,
    title: "Zusatzstoffkennzeichnung",
    description: <>Automatische Identifizierung und korrekte Kennzeichnung aller <Link to="/kennzeichnung#allergene-zusatzstoffe" className="text-primary hover:underline">kennzeichnungspflichtigen Zusatzstoffe</Link>.</>,
    items: ["Farbstoffe", "Konservierungsstoffe", "Süßungsmittel", "Geschmacksverstärker"]
  }, {
    icon: Gavel,
    title: "Rechtsanalyse LMIV",
    description: <>Vollständige Analyse nach <Link to="/faq?tab=gesetzesdatenbank" className="text-primary hover:underline">Lebensmittel-Informationsverordnung</Link>, deutschen Leitsätzen und EU-Verordnungen.</>,
    items: ["Gewichtsangaben bei Fleisch", "Mineralwasserverordnung", "Geschützte Bezeichnungen", "Irreführungsschutz"]
  }, {
    icon: Search,
    title: "Begriffsanalyse",
    description: <>Analyse geschützter oder problematischer Begriffe inklusive der dazugehörigen Rechtsgrundlage.</>,
    items: ["\"hausgemacht\", \"regional\"", "Döner Kebab Definition", "Schinken vs. Formfleisch", "Markenschutz"]
  }, {
    icon: Clock,
    title: "Schnelle Bearbeitung",
    description: <>Ihre <Link to="/speisekarte-hochladen" className="text-primary hover:underline">Speisen- und Getränkekarte</Link> wird in wenigen Minuten analysiert und rechtskonform aufbereitet.</>,
    items: ["OCR-Texterkennung", "KI-basierte Analyse", "Automatische Formatierung", "Sofortiger Download"]
  }, {
    icon: Award,
    title: "Professionelle Ausgabe",
    description: <>Erstellung von strukturierten Unterlagen wie <Link to="/demo#pruefbericht" className="text-primary hover:underline">PDF Analysebericht</Link> oder EXCEL Allergenliste.</>,
    items: ["Rechtskonforme Formatierung", "Professionelles Layout", "Detaillierte Angaben", "Handlungsempfehlungen"]
  }];
  const laws = ["LMIV", "LFGB", "LMIDV", <Link key="lmzdv" to="/faq?tab=gesetzesdatenbank" className="text-primary hover:underline">LMZDV</Link>, "Mineralwasserverordnung", "Gaststättengesetz", "Preisangabenverordnung", "Leitsätze für Fleisch", "Markenschutzgesetz", "und weitere Gesetze"];
  const advantages = [{
    title: <Link to="/vorteile" className="text-primary hover:underline">Rechtskonformität</Link>,
    description: <>Analyse nach allen relevanten EU- und nationalen Vorschriften. Alle Angaben sind LMIV-konform, klar nachvollziehbar und rechtlich belastbar.</>,
    details: "Garantierte 100 % LMIV-Konformität – für maximale Sicherheit bei Kontrollen.",
    metric: "100 %",
    metricLabel: "Konform ohne Kompromisse",
    icon: Shield
  }, {
    title: <Link to="/faq?tab=applicability" className="text-primary hover:underline">Schutz vor Bußgeldern</Link>,
    description: <>Vermeiden Sie teure Rechtsfolgen durch fehlerhafte oder fehlende <Link to="/kennzeichnung" className="text-primary hover:underline">Kennzeichnungen</Link>. Das System erkennt Verstöße frühzeitig – bevor Bußgelder entstehen.</>,
    details: "Bis zu 50.000 € können bei schwerwiegenden Verstößen fällig werden.",
    metric: "50.000 €",
    metricLabel: "max. Bußgeld",
    icon: ShieldAlert
  }, {
    title: "Zeitersparnis",
    description: "Automatisierte Analyse aller Speisen oder Getränke in Sekunden. Intelligente KI-Erkennung reduziert Ihren Aufwand bei jeder Analyse.",
    details: "Bis zu 95 % weniger manueller Aufwand im Vergleich zur Einzelanalyse.",
    metric: "95 %",
    metricLabel: "weniger Aufwand",
    icon: Clock
  }, {
    title: "Professionell",
    description: "Erfahrung aus fast 20 Jahren Lebensmittelkennzeichnung & Gastronomie. Praxisgerechte Bewertung, rechtskonform formuliert und dokumentiert.",
    details: "Top-Bewertung durch Betriebe und Prüfer gleichermaßen.",
    metric: "4.9★",
    metricLabel: "Bewertung",
    icon: Star
  }];
  const steps = [{
    number: "1",
    title: "Upload",
    description: "Laden Sie Ihre Speisen- und Getränkekarte als PDF oder Bild hoch",
    icon: Upload
  }, {
    number: "2",
    title: "Analyse",
    description: "Unsere KI analysiert automatisch alle Inhalte",
    icon: Search
  }, {
    number: "3",
    title: "Prüfen & Bestellen",
    description: "Sie prüfen die erkannten Daten und wählen Ihr Wunschpaket",
    icon: CheckCircle
  }, {
    number: "4",
    title: "Ergebnisse",
    description: "Erhalten Sie immer einen vollständigen Analysebericht inkl. Handlungsempfehlungen",
    icon: ClipboardList
  }];
  const handlePackageSelect = (packageId: string) => {
    setSelectedPackage(packageId);
    localStorage.setItem('selectedPackage', packageId);
  };
  const {
    data: dbStats
  } = useDatabaseStats();
  const stats = [{
    value: dbStats?.formattedTotalFoods || "7.400+",
    label: "Lebensmittel in Datenbank"
  }, {
    value: `${dbStats?.recognitionRate || 94} %`,
    label: "Automatische Erkennung"
  }, {
    value: "24/7",
    label: "Sofort verfügbar"
  }, {
    value: "Live",
    label: "Datenbank-Updates"
  }];

  // Mobile Pricing Plans - Alle 4 Pakete mit vollem Namen
  const mobilePricingPlans = dynamicPackages;
  return <>
      {isMobile ? <MobileAppLayout>
          <div className={isLandscape ? 'p-3 space-y-4' : 'p-4 space-y-8'}>
            <MobileHeroSection />
            
            <div className={isLandscape ? 'grid grid-cols-3 gap-2' : 'space-y-3'}>
              <MobileFeatureCard icon={Shield} title="Rechtskonform" description="100% LMIV-konform" />
              <MobileFeatureCard icon={Zap} title="In max. 4 min" description="Schnelle Analyse" />
              <MobileFeatureCard icon={FileText} title="PDF-Report" description="Sofort downloadbar" />
            </div>
            
            <div>
              <h2 className={`font-bold ${isLandscape ? 'text-base mb-2' : 'text-xl mb-4'}`}>Unsere Preise</h2>
              <div className="horizontal-scroll pb-4">
                {mobilePricingPlans.map(plan => <MobilePriceCard key={plan.id} id={plan.id} name={plan.name} price={plan.priceDisplay} period="pro Analyse" features={plan.features} recommended={plan.popular} disabled={plan.disabled} statusLabel={plan.statusLabel} />)}
              </div>
            </div>
          </div>
        </MobileAppLayout> : <Layout>
          {/* Hero Section */}
          <Section className="pt-32 pb-20">
            <SectionContainer>
              <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
                <div className="space-y-8">
                  <div className="space-y-6">
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground mb-4 md:mb-6">
                      Modernste KI-Technologie
                    </h1>
                    <h2 className="text-2xl lg:text-3xl font-semibold text-foreground mb-4">Rechtskonforme Speisen- und Getränkekarten </h2>
                    <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
                      Automatische Analyse auf <Link to="/kennzeichnung#allergene-zusatzstoffe" className="text-primary hover:underline">Allergene</Link>, <Link to="/kennzeichnung#allergene-zusatzstoffe" className="text-primary hover:underline">Zusatzstoffe</Link> und viele weiteren <Link to="/kennzeichnung" className="text-primary hover:underline">Pflichtangaben</Link>. Effizient nach geltenden Rechtsvorschriften in nur einem Scan.
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button variant="primary" size="lg" className="px-8 py-4" asChild>
                      <Link to="/speisekarte-hochladen">
                        Speisekarte hochladen
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" className="px-8 py-4" asChild>
                      <Link to="/demo">Demo ansehen</Link>
                    </Button>
                  </div>

                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6 lg:gap-8 pt-4">
                    <Badge variant="secondary" className="bg-primary/20 text-primary-foreground bg-primary border border-primary/30 hover:shadow-green transition-all duration-300">
                      Super einfach
                    </Badge>
                    <Badge variant="secondary" className="bg-primary/20 text-primary-foreground bg-primary border border-primary/30 hover:shadow-green transition-all duration-300">
                      KI-Analyse
                    </Badge>
                    <Badge variant="secondary" className="bg-primary/20 text-primary-foreground bg-primary border border-primary/30 hover:shadow-green transition-all duration-300">
                      LMIV-konform
                    </Badge>
                  </div>
                </div>

                <div className="relative">
                  <img src={heroImage} alt="Speisekarten-Compliance Illustration" className="w-full h-auto rounded-lg border border-border hover:border-primary hover:shadow-green transition-all duration-300" width={510} height={340} fetchPriority="high" decoding="async" />
                </div>
              </div>
            </SectionContainer>
          </Section>

          {/* Detailed Functions Section */}
          <Section id="vorteile" variant="muted">
            <SectionContainer>
              <SectionHeader title="Ihre Vorteile" subtitle={"Umfassende KI-gestützte Compliance-Prüfung.\nVollautomatisch, präzise und rechtskonform für Ihre Speisen- und Getränkekarte."} />
              <FeatureGrid features={detailedFunctions} />
            </SectionContainer>
          </Section>

          {/* Stats Section - Modernes Inline-Design */}
          <Section variant="muted" className="py-12">
            <SectionContainer>
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border border-primary/20 p-8">
                {/* Decorative background elements */}
                <div className="absolute top-0 left-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 right-0 w-40 h-40 bg-primary/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
                
                <div className="relative flex flex-wrap items-center justify-center gap-x-12 gap-y-6 md:gap-x-16 lg:gap-x-24">
                  {stats.map((stat, index) => <div key={index} className="flex items-center gap-4 group">
                      {/* Animated accent line */}
                      <div className="hidden sm:block w-px h-12 bg-gradient-to-b from-transparent via-primary/40 to-transparent group-hover:via-primary/70 transition-all duration-500" />
                      
                      <div className="text-center sm:text-left">
                        <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent group-hover:from-primary/80 group-hover:to-primary transition-all duration-300">
                          {stat.value}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1 group-hover:text-foreground/70 transition-colors duration-300">
                          {stat.label}
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </SectionContainer>
          </Section>

          {/* How it Works Section */}
          <Section id="wie-es-funktioniert" variant="muted">
            <SectionContainer>
              <SectionHeader title="So funktioniert's" subtitle="In nur wenigen Schritten zur vollständigen Compliance-Analyse" />
              <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6 lg:gap-x-4">
                {/* Connecting line (desktop only) */}
                <div className="hidden lg:block absolute top-[72px] left-[15%] right-[15%] h-0.5 bg-primary/15" />
                
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <div key={index} className="flex flex-col items-center text-center group">
                      {/* Circle with icon + number badge */}
                      <div className="relative mb-6">
                        {/* Large background circle */}
                        <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-primary/8 flex items-center justify-center group-hover:bg-primary/15 transition-all duration-500">
                          <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-primary stroke-[1.5]" />
                        </div>
                        {/* Number badge */}
                        <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shadow-md">
                          {step.number}
                        </div>
                      </div>
                      
                      <h3 className="text-lg sm:text-xl font-bold mb-2 text-foreground">{step.title}</h3>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-[220px]">{step.description}</p>
                    </div>
                  );
                })}
              </div>
            </SectionContainer>
          </Section>

          {/* Pricing Section */}
          <Section id="preise">
            <SectionContainer>
              <SectionHeader title="Unsere Preise" subtitle={<>Wir bieten Ihnen <Link to="/preise" className="text-primary hover:underline">3 Pakete für jeden Bedarf</Link> – von {dynamicPackages.filter(p => !p.disabled).reduce((min, p) => Math.min(min, p.price), Infinity)}€ bis {dynamicPackages.filter(p => !p.disabled).reduce((max, p) => Math.max(max, p.price), 0)}€</>} />
              
              {/* PackageSelector wie auf Upload-Seite */}
              <PackageSelector onPackageSelect={handlePackageSelect} selectedPackage={selectedPackage} onUploadClick={() => setShowUploadDialog(true)} />
              
              {/* Abo-Auswahl ist jetzt in den Paketkarten integriert */}
              
              {/* Behörden- & Druckereizugang */}
              <BusinessAccessCTA />
              
            </SectionContainer>
          </Section>

          {/* Legal Basis Section */}
          <Section className="hidden md:block">
            <SectionContainer>
              <SectionHeader title="Rechtskonforme Basis für Ihre Karte" subtitle="Unsere Datenbank wird kontinuierlich mit den neuesten Gesetzen, Verordnungen und Leitsätzen aktualisiert." />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                {laws.map((law, index) => <Card key={index} className="h-full p-3 md:p-4 text-center bg-card border border-border hover:border-primary hover:shadow-green transition-all duration-300">
                    <CardContent className="p-0 flex items-center justify-center h-full">
                      <span className="text-xs sm:text-sm font-medium text-foreground leading-tight break-words">{law}</span>
                    </CardContent>
                  </Card>)}
              </div>
            </SectionContainer>
          </Section>

          {/* Demo Section - Nach Rechtskonforme Basis */}
          <Section id="demo" variant="muted">
            <SectionContainer>
              <SectionHeader title="Demo ansehen" subtitle="Überzeugen Sie sich selbst von der Qualität unserer Analyse" />
              <div className="relative grid grid-cols-1 md:grid-cols-3 gap-y-12 gap-x-6 lg:gap-x-4">
                {/* Connecting line (desktop only) */}
                <div className="hidden md:block absolute top-[72px] left-[20%] right-[20%] h-0.5 bg-primary/15" />
                
                {[
                  { icon: FileText, title: "Original-Speisekarte", description: "Sehen Sie eine echte Speisekarte als Ausgangspunkt der Analyse" },
                  { icon: Search, title: "Detaillierter Analysebericht", description: "Vollständige Analyse mit allen erkannten Allergenen und Zusatzstoffen" },
                  { icon: Target, title: "Handlungsempfehlungen", description: "Konkrete Maßnahmen zur rechtskonformen Optimierung Ihrer Karte" },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex flex-col items-center text-center group">
                      <div className="relative mb-6">
                        <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-primary/8 flex items-center justify-center group-hover:bg-primary/15 transition-all duration-500">
                          <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-primary stroke-[1.5]" />
                        </div>
                        <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shadow-md">
                          {index + 1}
                        </div>
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold mb-2 text-foreground">{item.title}</h3>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-[240px]">{item.description}</p>
                    </div>
                  );
                })}
              </div>
              
              <div className="text-center mt-12">
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  In unserer interaktiven Demo können Sie den gesamten Ablauf einer Analyse erleben.
                </p>
                <Button variant="primary" size="lg" className="px-8 py-4" asChild>
                  <Link to="/demo">
                    Demo ansehen 
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </SectionContainer>
          </Section>

          {/* Upload Dialog */}
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogContent className="sm:max-w-xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-primary" />
                  Speisekarte hochladen
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <FileUpload onFileUpload={handleFileUpload} isProcessing={false} />
              </div>
            </DialogContent>
          </Dialog>


          {/* Advantages Section */}
          <Section>
            <SectionContainer>
              <SectionHeader title="Warum SpeisekartenCheck?" subtitle="Die Vorteile einer professionellen Compliance-Analyse" />
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {advantages.map((adv, index) => <Card key={index} className="h-full bg-card border border-border hover:shadow-lg hover:border-primary/40 transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                        <adv.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="text-2xl font-bold text-primary mb-1">{adv.metric}</div>
                      <div className="text-xs text-muted-foreground mb-3">{adv.metricLabel}</div>
                      <h3 className="text-lg font-bold mb-2 text-foreground">{adv.title}</h3>
                      <p className="text-sm text-muted-foreground">{adv.description}</p>
                    </CardContent>
                  </Card>)}
              </div>
            </SectionContainer>
          </Section>

          <CtaSection />


          <div className="max-w-6xl mx-auto px-4">
            <DisclaimerNotice />
          </div>
        </Layout>}
    </>;
};
export default Index;